/**
 * Stack Component Mapping Configuration
 * Maps each technology stack component to its layer, direct signals,
 * capability signals, and requirement triggers.
 * Used by buildStackAnalysis() in scoring-engine.js
 */

export const STACK_COMPONENT_MAP = {
  microsoft_entra_id: {
    layer_id: 'enforcement_layer',
    direct_signals: ['identity_platform'],
    capability_signals: ['mfa_all_users', 'mfa_privileged_users', 'shared_or_generic_accounts'],
    corroborating_fields: ['core_platforms'],
    required_when: [
      { field: 'mfa_all_users', condition: 'not_pass' },
      { field: 'mfa_privileged_users', condition: 'not_pass' },
      { field: 'identity_platform', condition: 'in', values: ['on_prem_active_directory', 'unknown'] }
    ],
    recommended_when: [
      { field: 'identity_platform', condition: 'eq', value: 'other' },
      { field: 'conditional_access', condition: 'not_pass' }
    ],
    primary_domains: ['identity_and_mfa']
  },

  conditional_access: {
    layer_id: 'enforcement_layer',
    direct_signals: ['conditional_access'],
    capability_signals: ['mfa_all_users', 'mfa_privileged_users', 'device_compliance_policies', 'byod_allowed'],
    corroborating_fields: [],
    required_when: [
      { field: 'conditional_access', condition: 'not_pass' },
      { field: 'mfa_all_users', condition: 'not_pass' },
      { field: 'mfa_privileged_users', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['identity_and_mfa', 'endpoint_management']
  },

  microsoft_intune: {
    layer_id: 'enforcement_layer',
    direct_signals: ['central_device_management', 'os_patch_management_method'],
    capability_signals: ['device_compliance_policies', 'corporate_devices_required', 'remote_config_or_wipe', 'central_patch_compliance_tracking'],
    corroborating_fields: [],
    required_when: [
      { field: 'central_device_management', condition: 'in', values: ['none', 'unknown'] },
      { field: 'remote_config_or_wipe', condition: 'not_pass' },
      { field: 'device_compliance_policies', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['endpoint_management', 'patch_management']
  },

  microsoft_defender_for_endpoint: {
    layer_id: 'enforcement_layer',
    direct_signals: ['vulnerability_tool'],
    capability_signals: ['application_execution_monitored', 'browser_security_settings_enforced'],
    corroborating_fields: [],
    required_when: [
      { field: 'application_execution_monitored', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['application_control', 'office_macros_and_user_hardening']
  },

  wdac_or_applocker: {
    layer_id: 'enforcement_layer',
    direct_signals: [],
    capability_signals: ['restrict_which_apps_can_run', 'allow_listing_in_place', 'users_can_install_software'],
    corroborating_fields: [],
    required_when: [
      { field: 'allow_listing_in_place', condition: 'not_pass' },
      { field: 'restrict_which_apps_can_run', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['application_control']
  },

  tenable: {
    layer_id: 'validation_layer',
    direct_signals: ['vulnerability_tool'],
    capability_signals: ['vulnerability_scanning_used', 'regular_vulnerability_scans', 'real_time_identification_of_unpatched_systems'],
    corroborating_fields: [],
    required_when: [
      { field: 'vulnerability_scanning_used', condition: 'not_pass' },
      { field: 'real_time_identification_of_unpatched_systems', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['vulnerability_management', 'patch_management'],
    mutually_exclusive_group: ['qualys']
  },

  qualys: {
    layer_id: 'validation_layer',
    direct_signals: ['vulnerability_tool'],
    capability_signals: ['vulnerability_scanning_used', 'regular_vulnerability_scans', 'real_time_identification_of_unpatched_systems'],
    corroborating_fields: [],
    required_when: [
      { field: 'vulnerability_scanning_used', condition: 'not_pass' },
      { field: 'real_time_identification_of_unpatched_systems', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['vulnerability_management', 'patch_management'],
    mutually_exclusive_group: ['tenable']
  },

  microsoft_sentinel: {
    layer_id: 'monitoring_layer',
    direct_signals: ['logging_tool'],
    capability_signals: ['centralised_logging', 'alerts_actively_monitored', 'admin_activity_logged_and_reviewed', 'incident_response_process'],
    corroborating_fields: [],
    required_when: [
      { field: 'centralised_logging', condition: 'not_pass' },
      { field: 'admin_activity_logged_and_reviewed', condition: 'not_pass' },
      { field: 'alerts_actively_monitored', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['logging_and_monitoring']
  },

  privileged_access_management: {
    layer_id: 'enforcement_layer',
    direct_signals: [],
    capability_signals: ['separate_admin_accounts', 'admin_activity_logged_and_reviewed', 'temporary_privilege_elevation', 'local_admin_rights_widespread'],
    corroborating_fields: [],
    required_when: [
      { field: 'separate_admin_accounts', condition: 'not_pass' },
      { field: 'temporary_privilege_elevation', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['privileged_access']
  },

  backup_platform: {
    layer_id: 'enforcement_layer',
    direct_signals: [],
    capability_signals: ['systems_backed_up', 'backup_frequency', 'offline_or_immutable_backups', 'last_successful_restore_test'],
    corroborating_fields: [],
    required_when: [
      { field: 'offline_or_immutable_backups', condition: 'not_pass' },
      { field: 'last_successful_restore_test', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['backups']
  },

  drata: {
    layer_id: 'compliance_layer',
    direct_signals: ['evidence_collection_method', 'compliance_management_approach'],
    capability_signals: ['disp_11_evidence_readiness', 'audit_evidence_capability'],
    corroborating_fields: [],
    required_when: [
      { field: 'evidence_collection_method', condition: 'in', values: ['manual_documents', 'none', 'unknown'] },
      { field: 'compliance_management_approach', condition: 'in', values: ['spreadsheet_based', 'ad_hoc_reactive', 'none'] }
    ],
    recommended_when: [
      { field: 'evidence_collection_method', condition: 'in', values: ['ticketing_system', 'spreadsheet_tracking'] }
    ],
    primary_domains: ['compliance_and_governance']
  },

  remediation_tracker: {
    layer_id: 'compliance_layer',
    direct_signals: ['remediation_timelines_tracked', 'vulnerabilities_tracked_and_remediated'],
    capability_signals: ['risk_register_maintained', 'compliance_pressure'],
    corroborating_fields: [],
    required_when: [
      { field: 'vulnerabilities_tracked_and_remediated', condition: 'not_pass' },
      { field: 'remediation_timelines_tracked', condition: 'not_pass' }
    ],
    recommended_when: [],
    primary_domains: ['vulnerability_management']
  },

  evidence_pack_generator: {
    layer_id: 'compliance_layer',
    direct_signals: ['evidence_collection_method'],
    capability_signals: ['disp_11_evidence_readiness', 'audit_evidence_capability'],
    corroborating_fields: [],
    required_when: [
      { field: 'evidence_collection_method', condition: 'in', values: ['manual_documents', 'none', 'unknown'] },
      { field: 'compliance_pressure', condition: 'in', values: ['immediate', 'high'] }
    ],
    recommended_when: [
      { field: 'evidence_collection_method', condition: 'in', values: ['ticketing_system', 'spreadsheet_tracking'] }
    ],
    primary_domains: ['compliance_and_governance']
  },

  disp_dashboard: {
    layer_id: 'compliance_layer',
    direct_signals: ['disp_status', 'frameworks_currently_tracked'],
    capability_signals: ['compliance_pressure'],
    corroborating_fields: ['primary_pain_drivers'],
    required_when: [
      { field: 'disp_status', condition: 'in', values: ['current_member', 'applying_within_3_months'] },
      { field: 'compliance_pressure', condition: 'in', values: ['immediate', 'high'] }
    ],
    recommended_when: [
      { field: 'disp_status', condition: 'in', values: ['applying_within_12_months', 'considering_disp'] }
    ],
    primary_domains: ['compliance_and_governance', 'organisation_profile']
  }
};

/**
 * Effective status scoring for stack_alignment_score (informational only)
 */
export const STACK_STATUS_SCORES = {
  implemented_effective: 100,
  implemented_partial: 65,
  present_but_ineffective: 35,
  implemented_limited: 25,
  planned_not_implemented: 15,
  not_present: 0,
  unknown: 10
};

/**
 * Normalized display status model (PRIMARY signal)
 * Maps internal effective_status to clean display status
 */
export const DISPLAY_STATUS_MAP = {
  implemented_effective: 'implemented',
  implemented_partial: 'partial',
  present_but_ineffective: 'needs_improvement',
  implemented_limited: 'needs_improvement',
  planned_not_implemented: 'planned',
  not_present: 'missing',
  unknown: 'planned'
};

/**
 * Display status labels and colors
 */
export const DISPLAY_STATUS_CONFIG = {
  implemented: { label: 'Implemented', color: 'green' },
  needs_improvement: { label: 'Needs Improvement', color: 'orange' },
  partial: { label: 'Partially Implemented', color: 'yellow' },
  missing: { label: 'Missing', color: 'red' },
  planned: { label: 'Planned', color: 'grey' }
};

/**
 * Requirement text (SECONDARY - descriptive, not badge)
 */
export const REQUIREMENT_TEXT = {
  required: 'Required - action needed to meet ML2',
  recommended: 'Recommended - improves maturity and automation',
  sufficient: 'No new tool required - focus on improving current implementation',
  alternative: 'Alternative option - equivalent capability already available',
  not_needed_yet: 'No new tool required - focus on improving current implementation'
};
